import { useState } from 'react';
import { Common } from '@thecvlb/design-system';

import {
  useDeleteDocumentMutation,
  useUploadDocumentsMutation
} from 'services/documents/documents';

import FileZone from 'features/FileZone';
import { notifyError, notifyToast } from 'shared/Toast/Toast';

import { VerifyInsuranceProps } from './verifyInsurance.types';

const VerifyInsurance: React.FC<VerifyInsuranceProps> = ({
  isOpen,
  onClose,
  type,
  filesStatus,
  setFilesStatus,
  accessToken
}) => {
  const [deleteDocument, { isLoading: deleteDocumentsLoading }] = useDeleteDocumentMutation();
  const [uploadDocuments] = useUploadDocumentsMutation();
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);

  const handleClick = async (items: File[]) => {
    try {
      const images = items?.filter((item) =>
        ['image/png', 'image/jpg', 'image/jpeg', 'image/heic'].includes(item.type)
      );

      if (images.length !== 1) {
        return notifyToast('Please select one image');
      }

      let convertedFile = images[0];
      if (convertedFile.size > 10000000) {
        throw new Error(
          `We apologize, but the image you've uploaded exceed the maximum allowable size 10MB. To continue, please consider resizing the image or selecting smaller one.`
        );
      }
      if (convertedFile.type === 'image/heic') {
        const heic2any = (await import('heic2any')).default;
        const imageBlob = await heic2any({
          blob: convertedFile,
          toType: 'image/jpeg'
        });

        // Create a new File object with the converted image
        convertedFile = new File(
          [imageBlob as Blob],
          convertedFile.name.replace('.heic', '.jpeg'),
          {
            type: 'image/jpeg'
          }
        );
      }

      setFilesStatus({ ...filesStatus, fileStatus: 'processing' });
      const formData = new FormData();
      formData.append('patientDocumentFile', convertedFile);
      formData.append('category', 'insurance');
      formData.append(
        'insuranceCard',
        JSON.stringify({
          rank: 1,
          side: type === 'front' ? 1 : 2
        })
      );

      const res = await uploadDocuments({ accessToken, body: formData }).unwrap();
      setFilesStatus({
        _id: res.data._id,
        fileName: images[0].name,
        filePath: res.data.filePath,
        fileStatus: 'success'
      });
    } catch (e) {
      setFilesStatus({ fileName: '', filePath: '', fileStatus: 'initial' });
      notifyError((e as Error).message || "Can't upload file");
    }
  };

  const handleDelete = (id: string) => {
    setDocumentToDelete(id);
    deleteDocument({ accessToken, documentId: id })
      .unwrap()
      .then(() => setFilesStatus({ fileName: '', filePath: '', fileStatus: 'initial' }))
      .finally(() => setDocumentToDelete(null));
  };

  return (
    <Common.Modal close={onClose} isOpen={isOpen} size="lg">
      <div className="flex flex-col gap-6 max-md:pt-6 md:mx-auto md:max-w-[280px] md:items-center">
        <h1 className="text-center text-2xl font-bold text-primary-700 max-md:hidden">
          Add your insurance info
        </h1>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col items-center">
            <Common.Icon className="size-12 rounded-full text-primary-400" name="identification" />
            <h1 className="mt-4 text-m2xl font-bold text-primary-700 first-letter:uppercase md:text-mBase">
              {type}
            </h1>
            <p className="mt-4 text-center text-primary-700 md:mt-2 md:text-sm">
              Upload a photo of the {type} of your insurance card.
            </p>
          </div>
          <div className="md:max-w-[280px]">
            <FileZone
              accept="image/png, image/jpeg, image/jpg, .heic"
              loading={deleteDocumentsLoading && documentToDelete === filesStatus._id}
              placeholder="Upload photo"
              status={filesStatus}
              type={type}
              onDelete={handleDelete}
              onSelect={handleClick}
            />
          </div>
          {filesStatus.fileStatus === 'error' && (
            <Common.Alert type="error" colorableBackground>
              We had trouble verifying your insurance. Please fix the errors on the following pages
              in order to complete your onboarding.
            </Common.Alert>
          )}
        </div>
        <Common.Button
          className="mx-auto"
          color="blue"
          dataTestId="continue_btn"
          disabled={filesStatus.fileStatus !== 'success'}
          fullWidthOnMobile
          onClick={onClose}
        >
          Continue
        </Common.Button>
      </div>
    </Common.Modal>
  );
};

export default VerifyInsurance;
